import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, Play } from 'lucide-react';
import { adminApi, type AdminOrgDetail } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ReasonModal } from '@/components/ReasonModal';
import { StatusBadge } from '@/components/StatusBadge';
import { useErrorToast, useToast } from '@/lib/toast';
import { formatTime, timeAgo } from '@/util/format';

type Confirm = 'suspend' | 'unsuspend' | null;

interface Field {
  label: string;
  value: React.ReactNode;
}

function FieldGrid({ fields }: { fields: Field[] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
      {fields.map((f) => (
        <div key={f.label} className="grid grid-cols-[160px_1fr] gap-2">
          <dt className="text-muted-foreground">{f.label}</dt>
          <dd className="break-all">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function OrgDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [data, setData] = useState<AdminOrgDetail | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const onError = useErrorToast();
  const toast = useToast();

  async function refresh() {
    try {
      const r = await adminApi.getOrg(id);
      setData(r.data);
    } catch (e) {
      onError(e);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!data) {
    return <div className="py-12 text-center text-muted-foreground">Loading…</div>;
  }

  const { org, members, recentRuns, recentAudit } = data;
  const status = org.status ?? 'active';
  const fields: Field[] = [
    { label: 'Org id', value: <span className="font-mono">{org.id}</span> },
    { label: 'Slug', value: <span className="font-mono">{org.slug}</span> },
    { label: 'Created', value: formatTime(org.createdAt) },
    { label: 'Plan', value: org.plan ?? 'free' },
    {
      label: 'Stripe customer',
      value: <span className="font-mono">{org.stripeCustomerId ?? '—'}</span>,
    },
    { label: 'Period end', value: formatTime(org.currentPeriodEnd) },
  ];
  if (status === 'suspended') {
    fields.push(
      { label: 'Suspended at', value: formatTime(org.suspendedAt) },
      { label: 'Suspended by', value: <span className="font-mono">{org.suspendedBy ?? '—'}</span> },
      { label: 'Reason', value: org.suspendedReason ?? '—' },
    );
  }

  return (
    <>
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/orgs">
            <ArrowLeft />
            All organizations
          </Link>
        </Button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{org.name}</h1>
          <StatusBadge status={status} />
        </div>
      </div>

      <div className="flex gap-2">
        {status === 'suspended' ? (
          <Button onClick={() => setConfirm('unsuspend')}>
            <Play />
            Unsuspend
          </Button>
        ) : (
          <Button variant="destructive" onClick={() => setConfirm('suspend')}>
            <Ban />
            Suspend
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Plan & metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <FieldGrid fields={fields} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Members ({members.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>
                    {m.name ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{m.role}</TableCell>
                  <TableCell className="text-muted-foreground">{timeAgo(m.createdAt)}</TableCell>
                </TableRow>
              ))}
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="h-16 text-center text-muted-foreground">
                    No members.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run id</TableHead>
                <TableHead>Flow</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Completed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentRuns.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.id}</TableCell>
                  <TableCell className="font-mono text-xs">{r.flowId}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell className="text-muted-foreground">{timeAgo(r.startedAt)}</TableCell>
                  <TableCell className="text-muted-foreground">{timeAgo(r.completedAt)}</TableCell>
                </TableRow>
              ))}
              {recentRuns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-16 text-center text-muted-foreground">
                    No runs.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent audit</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Resource</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentAudit.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="text-muted-foreground">{timeAgo(a.createdAt)}</TableCell>
                  <TableCell className="font-mono text-xs">{a.eventType}</TableCell>
                  <TableCell>
                    {a.actorType ?? 'user'}
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {a.adminUserId ?? a.userId ?? '—'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {a.resourceType ?? '—'}
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {a.resourceId ?? ''}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {recentAudit.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="h-16 text-center text-muted-foreground">
                    No audit events.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ReasonModal
        open={confirm === 'suspend'}
        title={`Suspend "${org.name}"?`}
        description="All API mutations from this org's users will return 403 until unsuspended."
        confirmLabel="Suspend"
        confirmKind="destructive"
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={async (reason) => {
          try {
            await adminApi.suspendOrg(org.id, reason);
            toast.success('Suspended.');
            await refresh();
          } catch (e) {
            onError(e);
          }
        }}
      />
      <ReasonModal
        open={confirm === 'unsuspend'}
        title={`Unsuspend "${org.name}"?`}
        description="The org regains full access immediately."
        confirmLabel="Unsuspend"
        confirmKind="default"
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={async (reason) => {
          try {
            await adminApi.unsuspendOrg(org.id, reason);
            toast.success('Unsuspended.');
            await refresh();
          } catch (e) {
            onError(e);
          }
        }}
      />
    </>
  );
}
